import { create } from 'zustand';
import { Doctor } from '@/types';
import { supabase } from '../supabase';

interface DoctorsState {
  doctors: Doctor[];
  addDoctor: (doctor: Omit<Doctor, 'id'>) => Promise<void>;
  updateDoctor: (doctor: Doctor) => Promise<void>;
  fetchDoctors: () => Promise<void>;
}

function loadDoctorsCache() {
  try {
    const cache = localStorage.getItem('doctors-cache');
    if (cache) return JSON.parse(cache);
  } catch {}
  return [];
}
function saveDoctorsCache(doctors: Doctor[]) {
  try {
    localStorage.setItem('doctors-cache', JSON.stringify(doctors));
  } catch {}
}

export const useDoctorsStore = create<DoctorsState>()((set, get) => ({
  doctors: loadDoctorsCache(),
  
  fetchDoctors: async () => {
    try {
      const { data, error } = await supabase
        .from('doctors')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ doctors: data || [] });
      saveDoctorsCache(data || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  },

  addDoctor: async (doctor) => {
    try {
      // Check if current user is admin
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        throw new Error('You must be logged in to add doctors');
      }

      // Get the current user's profile to check their role
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', currentUser.id)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        throw new Error('Failed to verify admin permissions');
      }

      if (profile.role !== 'admin') {
        throw new Error('Only administrators can add doctors');
      }

      const { data, error } = await supabase
        .from('doctors')
        .insert([{
          ...doctor,
          active: true,      // Admin-controlled: employed/approved
          available: false,  // Doctor/user-controlled: on shift/ready for assignment
          patients_today: 0  // This is the database column name
        }]);

      if (error) throw error;

      // Since we're not getting the inserted data back, we'll need to refetch
      // or construct the doctor object manually
      const newDoctor: Doctor = {
        ...doctor,
        id: '', // We'll need to refetch to get the actual ID
        active: true,
        available: false,
        patientsToday: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const currentState = get();
      const updated = [...currentState.doctors, newDoctor];
      set({ doctors: updated });
      saveDoctorsCache(updated);
      
      // Refetch to get the actual data with ID
      await get().fetchDoctors();
    } catch (error) {
      console.error('Error adding doctor:', error);
      throw error;
    }
  },
  
  updateDoctor: async (updatedDoctor) => {
    try {
      console.log('=== Starting doctor update ===');
      console.log('Doctor to update:', updatedDoctor);
      
      // Check if current user is admin
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      console.log('Current user:', currentUser);
      
      if (!currentUser) {
        throw new Error('You must be logged in to update doctors');
      }

      // Get the current user's profile to check their role
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', currentUser.id)
        .single();

      console.log('User profile:', profile);
      console.log('Profile error:', profileError);

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        throw new Error('Failed to verify admin permissions');
      }

      console.log('User role:', profile.role);
      
      if (profile.role !== 'admin') {
        console.log('User is not admin, throwing error');
        throw new Error('Only administrators can update doctors');
      }

      console.log('User is admin, proceeding with update');
      console.log('Updating doctor with data:', updatedDoctor);
      
      // Map the Doctor interface fields to database column names
      // Only include fields that actually exist in the doctors table
      const updateData: any = {};

      // Only add fields that exist in the database and are not undefined
      if (updatedDoctor.name !== undefined) {
        updateData.name = updatedDoctor.name;
      }
      if (updatedDoctor.specialization !== undefined) {
        updateData.specialization = updatedDoctor.specialization;
      }
      if (updatedDoctor.registrationNumber !== undefined) {
        updateData.registration_number = updatedDoctor.registrationNumber;
      }
      if (updatedDoctor.status !== undefined) {
        updateData.status = updatedDoctor.status;
      }
      if (updatedDoctor.patientsToday !== undefined) {
        updateData.patients_today = updatedDoctor.patientsToday;
      }
      if (updatedDoctor.contactNumber !== undefined) {
        updateData.contact_number = updatedDoctor.contactNumber;
      }
      if (updatedDoctor.active !== undefined) {
        updateData.active = updatedDoctor.active;
      }
      if (updatedDoctor.available !== undefined) {
        updateData.available = updatedDoctor.available;
      }

      // Always update the updated_at timestamp
      updateData.updated_at = new Date().toISOString();

      console.log('Initial update data:', updateData);

      console.log('Final update data to send to Supabase:', updateData);
      console.log('Doctor ID to update:', updatedDoctor.id);

      const { error } = await supabase
        .from('doctors')
        .update(updateData)
        .eq('id', updatedDoctor.id);

      if (error) {
        console.error('Supabase update error:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      console.log('Doctor updated successfully');

      const currentState = get();
      const updated = currentState.doctors.map((doctor) =>
        doctor.id === updatedDoctor.id ? updatedDoctor : doctor
      );
      set({ doctors: updated });
      saveDoctorsCache(updated);
    } catch (error) {
      console.error('Error updating doctor:', error);
      throw error;
    }
  }
}));
